import React, { useState, useEffect } from 'react';
import { FaChevronDown } from 'react-icons/fa';
import { api } from '../../lib/api';
import {
  BadgeSection,
  BadgeSectionHeader,
  BadgeHeaderIcon,
  BadgeSectionTitle,
  BadgeSectionSubtitle,
  BadgeCategoriesContainer,
  CategoryCard,
  CategoryHeader,
  CategoryHeaderLeft,
  CategoryEmoji,
  CategoryTitle,
  BadgeCount,
  CategoryDescription,
  ToggleIcon,
  BadgeGrid,
  BadgeCard,
  PointsBadge,
  BadgeImageWrapper,
  BadgeImageContainer,
  BadgeImageSkeleton,
  BadgeImage,
  BadgeName,
  BadgeDescription,
  LoadingWrapper,
  Spinner,
} from './styles';

// Helper function to extract emoji from category name
const extractEmoji = (text) => {
  if (!text) return '🏆';

  // Try to get first grapheme cluster (works for all emojis including multi-codepoint ones)
  if (typeof Intl !== 'undefined' && Intl.Segmenter) {
    const segmenter = new Intl.Segmenter('en', { granularity: 'grapheme' });
    const segments = Array.from(segmenter.segment(text));
    if (segments.length > 0) {
      const firstChar = segments[0].segment;
      // Check if it's an emoji by testing against emoji regex
      const emojiRegex = /[\p{Emoji}\p{Emoji_Component}]/u;
      if (emojiRegex.test(firstChar)) {
        return firstChar;
      }
    }
  } else {
    // Fallback: Use Array.from to properly split Unicode
    const chars = Array.from(text);
    if (chars.length > 0) {
      const firstChar = chars[0];
      // Check if it's an emoji
      const emojiRegex = /[\p{Emoji}\p{Emoji_Component}]/u;
      if (emojiRegex.test(firstChar)) {
        return firstChar;
      }
    }
  }

  return '🏆';
};

// Helper function to extract title without emoji
const extractTitle = (text) => {
  if (!text) return '';

  // Try to remove first grapheme cluster if it's an emoji
  if (typeof Intl !== 'undefined' && Intl.Segmenter) {
    const segmenter = new Intl.Segmenter('en', { granularity: 'grapheme' });
    const segments = Array.from(segmenter.segment(text));
    if (segments.length > 0) {
      const firstChar = segments[0].segment;
      const emojiRegex = /[\p{Emoji}\p{Emoji_Component}]/u;
      if (emojiRegex.test(firstChar)) {
        return text.slice(segments[0].segment.length).trim();
      }
    }
  } else {
    // Fallback: Use Array.from
    const chars = Array.from(text);
    if (chars.length > 0) {
      const firstChar = chars[0];
      const emojiRegex = /[\p{Emoji}\p{Emoji_Component}]/u;
      if (emojiRegex.test(firstChar)) {
        return chars.slice(1).join('').trim();
      }
    }
  }

  return text.trim();
};

const HowToEarnBadges = () => {
  const [badgesData, setBadgesData] = useState([]);
  const [badgesLoading, setBadgesLoading] = useState(true);
  const [expandedCategories, setExpandedCategories] = useState({});

  // Fetch badges data
  useEffect(() => {
    const controller = new AbortController();

    const fetchBadges = async () => {
      try {
        const data = await api.badges.getAll({ signal: controller.signal });
        if (data.badges) {
          setBadgesData(data.badges);
          // Expand first category by default
          const firstCategoryId = data.badges[0]?.category?._id;
          if (firstCategoryId) {
            setExpandedCategories({ [firstCategoryId]: true });
          }
        }
        setBadgesLoading(false);
      } catch (err) {
        if (err.name !== 'AbortError' && err.message !== 'Request was cancelled') {
          console.error('Failed to load badges data:', err);
          setBadgesLoading(false);
        }
      }
    };
    fetchBadges();

    return () => controller.abort();
  }, []);

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  // Group badges by category
  const groupedBadges = () => {
    const groups = {};
    badgesData.forEach(badge => {
      const categoryId = badge.category._id;
      if (!groups[categoryId]) {
        groups[categoryId] = {
          category: badge.category,
          badges: []
        };
      }
      groups[categoryId].badges.push(badge);
    });
    return Object.values(groups);
  };

  const handleImageLoad = (e) => {
    e.target.style.opacity = '1';
    e.target.previousSibling.style.display = 'none';
  };

  return (
    <BadgeSection>
      <BadgeSectionHeader>
        <BadgeHeaderIcon>🎯</BadgeHeaderIcon>
        <BadgeSectionTitle>How to Earn Badges</BadgeSectionTitle>
        <BadgeSectionSubtitle>Complete activities to unlock achievements and earn points</BadgeSectionSubtitle>
      </BadgeSectionHeader>

      {badgesLoading ? (
        <LoadingWrapper>
          <Spinner />
          <p>Loading badges...</p>
        </LoadingWrapper>
      ) : (
        <BadgeCategoriesContainer>
          {groupedBadges().map((group) => (
            <CategoryCard key={group.category._id}>
              <CategoryHeader
                onClick={() => toggleCategory(group.category._id)}
                $isExpanded={expandedCategories[group.category._id]}
              >
                <CategoryHeaderLeft>
                  <CategoryEmoji>
                    {extractEmoji(group.category.name)}
                  </CategoryEmoji>
                  <div>
                    <CategoryTitle>
                      {extractTitle(group.category.name)}
                      <BadgeCount>({group.badges.length} badges)</BadgeCount>
                    </CategoryTitle>
                    <CategoryDescription>{group.category.description}</CategoryDescription>
                  </div>
                </CategoryHeaderLeft>
                <ToggleIcon $isExpanded={expandedCategories[group.category._id]}>
                  <FaChevronDown />
                </ToggleIcon>
              </CategoryHeader>

              {expandedCategories[group.category._id] && (
                <BadgeGrid>
                  {group.badges.map((badge, index) => (
                    <BadgeCard key={badge._id} style={{ '--index': index }}>
                      <PointsBadge>{badge.points} pts</PointsBadge>
                      <BadgeImageWrapper>
                        <BadgeImageContainer>
                          <BadgeImageSkeleton />
                          <BadgeImage
                            src={badge.imageUrl}
                            alt={badge.name}
                            loading="lazy"
                            onLoad={handleImageLoad}
                          />
                        </BadgeImageContainer>
                      </BadgeImageWrapper>
                      <BadgeName>{badge.name}</BadgeName>
                      <BadgeDescription>{badge.description}</BadgeDescription>
                    </BadgeCard>
                  ))}
                </BadgeGrid>
              )}
            </CategoryCard>
          ))}
        </BadgeCategoriesContainer>
      )}
    </BadgeSection>
  );
};

export default HowToEarnBadges;
